import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { ClearableInput, ListMenu, ListMenuItem } from '@rio-cloud/rio-uikit';
import { SECTION_IDS, GROUP_NAMES, MENU_ITEM_LABELS, MENU_ITEM_KEYS, SEARCH_PLACEHOLDER, FILTER_IDS } from '~/features/devices/constants/filterMenuConstants';

interface DevicesFiltersMenuProps {
    onSectionClick: (sectionId: string) => void;
}

const DevicesFiltersMenu: React.FC<DevicesFiltersMenuProps> = ({ onSectionClick }) => {
    const [searchTerm, setSearchTerm] = useState<string>('');

    const createMenuItem = (label: string, filterId: string) => (
        <span onClick={() => onSectionClick(filterId)} style={{ fontSize: '12px' }}>
            {label.startsWith('intl-msg:') ? <FormattedMessage id={label} defaultMessage={label} /> : label}
        </span>
    );

    const allMenuItems: ListMenuItem[] = [
        {
            group: <span className={'text-size-h6'} style={{ fontWeight: 'bold', fontFamily: 'MANEUROPECONDENSED' }}><FormattedMessage id={GROUP_NAMES.FAVORITES} defaultMessage='Favorites'/></span>,
            navItems: [
                { key: 'favorites', item: createMenuItem(MENU_ITEM_LABELS.VIEW_FAVORITES, SECTION_IDS.FAVORITES), isSubItem: true },
            ],
        },
        {
            group: <span className={'text-size-h6'} style={{ fontWeight: 'bold', fontFamily: 'MANEUROPECONDENSED' }}>{GROUP_NAMES.DEVICE_FILTERS}</span>,
            navItems: [
                { key: MENU_ITEM_KEYS.DEVICE_TYPE, item: createMenuItem(MENU_ITEM_LABELS.DEVICE_TYPE, FILTER_IDS.DEVICE_TYPE), isSubItem: true },
                { key: MENU_ITEM_KEYS.VEHICLE_VARIANT, item: createMenuItem(MENU_ITEM_LABELS.VEHICLE_VARIANT, FILTER_IDS.VEHICLE_VARIANT), isSubItem: true },
                { key: MENU_ITEM_KEYS.HW_VARIANT_PREFIX, item: createMenuItem(MENU_ITEM_LABELS.HW_VARIANT_PREFIX, FILTER_IDS.HW_VARIANT_PREFIX), isSubItem: true },
                { key: MENU_ITEM_KEYS.HW_VARIANTS, item: createMenuItem(MENU_ITEM_LABELS.HW_VARIANTS, FILTER_IDS.HW_VARIANTS), isSubItem: true },
            ],
        },
        {
            group: <span className={'text-size-h6'} style={{ fontWeight: 'bold', fontFamily: 'MANEUROPECONDENSED' }}>{GROUP_NAMES.STATUS_FILTERS}</span>,
            navItems: [
                { key: MENU_ITEM_KEYS.DEVICE_HEALTH_TYPES, item: createMenuItem(MENU_ITEM_LABELS.DEVICE_HEALTH_TYPES, FILTER_IDS.DEVICE_HEALTH_TYPES), isSubItem: true },
                { key: MENU_ITEM_KEYS.DEVICE_STATUS, item: createMenuItem(MENU_ITEM_LABELS.DEVICE_STATUS, FILTER_IDS.DEVICE_STATUS), isSubItem: true },
                { key: MENU_ITEM_KEYS.PROD_MODE, item: createMenuItem(MENU_ITEM_LABELS.PROD_MODE, FILTER_IDS.PROD_MODE), isSubItem: true },
                { key: MENU_ITEM_KEYS.IVD_MISMATCH, item: createMenuItem(MENU_ITEM_LABELS.IVD_MISMATCH, FILTER_IDS.IVD_MISMATCH), isSubItem: true },
                { key: MENU_ITEM_KEYS.BLOCKED, item: createMenuItem(MENU_ITEM_LABELS.BLOCKED, FILTER_IDS.BLOCKED), isSubItem: true },
                { key: MENU_ITEM_KEYS.REPLACED, item: createMenuItem(MENU_ITEM_LABELS.REPLACED, FILTER_IDS.REPLACED), isSubItem: true },
                { key: MENU_ITEM_KEYS.IN_CUSTOMER_FLEET, item: createMenuItem(MENU_ITEM_LABELS.IN_CUSTOMER_FLEET, FILTER_IDS.IN_CUSTOMER_FLEET), isSubItem: true },
                { key: MENU_ITEM_KEYS.TEST_RELEASES_ACTIVE, item: createMenuItem(MENU_ITEM_LABELS.TEST_RELEASES_ACTIVE, FILTER_IDS.TEST_RELEASES_ACTIVE), isSubItem: true },
            ],
        },
        {
            group: <span className={'text-size-h6'} style={{ fontWeight: 'bold', fontFamily: 'MANEUROPECONDENSED' }}>{GROUP_NAMES.SOFTWARE_FILTERS}</span>,
            navItems: [
                { key: MENU_ITEM_KEYS.ESSENTIAL_IMAGE, item: createMenuItem(MENU_ITEM_LABELS.ESSENTIAL_IMAGE, FILTER_IDS.ESSENTIAL_IMAGE), isSubItem: true },
                { key: MENU_ITEM_KEYS.DISTRO_VERSION, item: createMenuItem(MENU_ITEM_LABELS.DISTRO_VERSION, FILTER_IDS.DISTRO_VERSION), isSubItem: true },
                { key: MENU_ITEM_KEYS.SW_VERSION, item: createMenuItem(MENU_ITEM_LABELS.SW_VERSION, FILTER_IDS.SW_VERSION), isSubItem: true },
                { key: MENU_ITEM_KEYS.INSTALLED_DELIVERABLES, item: createMenuItem(MENU_ITEM_LABELS.INSTALLED_DELIVERABLES, FILTER_IDS.INSTALLED_DELIVERABLES), isSubItem: true },
                { key: MENU_ITEM_KEYS.HAS_MMT_PROPERTIES, item: createMenuItem(MENU_ITEM_LABELS.HAS_MMT_PROPERTIES, FILTER_IDS.HAS_MMT_PROPERTIES), isSubItem: true },
                { key: MENU_ITEM_KEYS.MMT_HEAD_UNIT, item: createMenuItem(MENU_ITEM_LABELS.MMT_HEAD_UNIT, FILTER_IDS.MMT_HEAD_UNIT), isSubItem: true },
                { key: MENU_ITEM_KEYS.MMT_FUNCTIONS, item: createMenuItem(MENU_ITEM_LABELS.MMT_FUNCTIONS, FILTER_IDS.MMT_FUNCTIONS), isSubItem: true },
                { key: MENU_ITEM_KEYS.VEHICLE_ENERGY_TYPE, item: createMenuItem(MENU_ITEM_LABELS.VEHICLE_ENERGY_TYPE, FILTER_IDS.VEHICLE_ENERGY_TYPE), isSubItem: true },
            ],
        },
        {
            group: <span className={'text-size-h6'} style={{ fontWeight: 'bold', fontFamily: 'MANEUROPECONDENSED' }}>{GROUP_NAMES.INCLUDE_DEVICES}</span>,
            navItems: [
                { key: MENU_ITEM_KEYS.GROUPS, item: createMenuItem(MENU_ITEM_LABELS.GROUPS, FILTER_IDS.GROUPS), isSubItem: true },
                { key: MENU_ITEM_KEYS.DEVICE_WHITELIST, item: createMenuItem(MENU_ITEM_LABELS.DEVICE_WHITELIST, FILTER_IDS.DEVICE_WHITELIST), isSubItem: true },
                { key: MENU_ITEM_KEYS.VINS_WHITELIST, item: createMenuItem(MENU_ITEM_LABELS.VINS_WHITELIST, FILTER_IDS.VINS_WHITELIST), isSubItem: true },
                { key: MENU_ITEM_KEYS.SHORT_VINS_WHITELIST, item: createMenuItem(MENU_ITEM_LABELS.SHORT_VINS_WHITELIST, FILTER_IDS.SHORT_VINS_WHITELIST), isSubItem: true },
            ],
        },
        {
            group: <span className={'text-size-h6'} style={{ fontWeight: 'bold', fontFamily: 'MANEUROPECONDENSED' }}>{GROUP_NAMES.EXCLUDE_DEVICES}</span>,
            navItems: [
                { key: MENU_ITEM_KEYS.GROUPS_BLACKLIST, item: createMenuItem(MENU_ITEM_LABELS.GROUPS_BLACKLIST, FILTER_IDS.GROUPS_BLACKLIST), isSubItem: true },
                { key: MENU_ITEM_KEYS.DEVICE_BLACKLIST, item: createMenuItem(MENU_ITEM_LABELS.DEVICE_BLACKLIST, FILTER_IDS.DEVICE_BLACKLIST), isSubItem: true },
            ],
        },
        {
            group: <span className={'text-size-h6'} style={{ fontWeight: 'bold', fontFamily: 'MANEUROPECONDENSED' }}>{GROUP_NAMES.OTHER_FILTERS}</span>,
            navItems: [
                { key: MENU_ITEM_KEYS.MILEAGE, item: createMenuItem(MENU_ITEM_LABELS.MILEAGE, FILTER_IDS.MILEAGE), isSubItem: true },
                { key: MENU_ITEM_KEYS.SPARE_PART_NUMBER, item: createMenuItem(MENU_ITEM_LABELS.SPARE_PART_NUMBER, FILTER_IDS.SPARE_PART_NUMBER), isSubItem: true },
            ],
        },
    ];

    const filteredMenuItems = useMemo(() => {
        if (!searchTerm) return allMenuItems;

        const searchLower = searchTerm.toLowerCase();

        return allMenuItems.map(group => {
            const matchingItems = group.navItems.filter(item => {
                const itemText = typeof item.item.props.children === 'string'
                    ? item.item.props.children
                    : item.item.props.children.props?.children || '';
                return itemText.toLowerCase().includes(searchLower);
            });

            return {
                ...group,
                navItems: matchingItems
            };
        }).filter(group => group.navItems.length > 0);
    }, [searchTerm, allMenuItems]);

    return (
        <div className='col-3 rounded bg-white shadow-smooth'>
            <div className='input-group margin-bottom-10 margin-top-10'>
                <span className='input-group-addon'>
                    <span className='rioglyph rioglyph-search' aria-hidden='true' />
                </span>
                <ClearableInput
                    value={searchTerm}
                    onChange={(newValue: string) => setSearchTerm(newValue)}
                    onClear={() => setSearchTerm('')}
                    placeholder={SEARCH_PLACEHOLDER}
                />
            </div>
            <div className='overflow-auto' style={{ height: '450px' }}>
                <ListMenu menuItems={filteredMenuItems} />
            </div>
        </div>
    );
};

export default DevicesFiltersMenu;
